// @oasis-permission-inherits: /jerboa-editor-session (jerboa:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  formatDurationMs,
  formatMb,
  formatTimestamp,
} from "~/modules/jerboa-client-preview/formatters";
import { AverageDurationChart } from "~/modules/jerboa-editor-session/average-duration-chart";
import { CrashFreeRateChart } from "~/modules/jerboa-editor-session/crash-free-rate-chart";
import { ClientInteractionsSection } from "~/modules/jerboa-editor-session/interactions-section";
import {
  fetchJerboaEditorSessionAnalysisQuery,
  fetchJerboaEditorSessionsQuery,
} from "~/modules/jerboa-editor-session/queries";

const searchSchema = z.object({
  page: z.number().optional(),
  projectId: z.string().optional(),
});

const ANALYSIS_DAYS = 7;

export const Route = createFileRoute("/_dash/jerboa-editor-session/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const params = useMemo(() => ({ projectId }), [projectId]);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchJerboaEditorSessionsQuery.useInfiniteQuery({ params });

  const { data: analysisData } = fetchJerboaEditorSessionAnalysisQuery.useQuery({
    params: { days: ANALYSIS_DAYS },
  });

  const sessions = useMemo(() => data?.pages.flatMap((page) => page.page) ?? [], [data?.pages]);

  if (error) {
    return (
      <div className="px-6 py-4 text-sm text-destructive">
        Failed to load editor sessions: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-4">
      <div>
        <h1 className="text-2xl font-bold">Client Sessions</h1>
        <p className="text-sm text-muted-foreground">
          Per editor mount → unmount session health. Crash-free rate is the headline reliability
          metric; median / P90 session duration tracks how long users sustain an editing session
          before exiting.
        </p>
      </div>

      {analysisData && analysisData.analysis.buckets.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium">Crash-free session rate</p>
              <p className="text-xs text-muted-foreground">last 7 days — 2-hour buckets</p>
            </div>
            <CrashFreeRateChart buckets={analysisData.analysis.buckets} />
          </div>
          <div className="rounded-md border bg-muted/20 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium">Average time spent per session</p>
              <p className="text-xs text-muted-foreground">last 7 days — 2-hour buckets</p>
            </div>
            <AverageDurationChart buckets={analysisData.analysis.buckets} />
          </div>
        </div>
      )}

      {analysisData && analysisData.analysis.buckets.length > 0 && (
        <ClientInteractionsSection buckets={analysisData.analysis.buckets} days={ANALYSIS_DAYS} />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Created</TableHead>
            <TableHead>Project ID</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead className="text-right">Peak reserved</TableHead>
            <TableHead className="text-right">Peak live</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : sessions.map((s) => (
                <TableRow
                  key={s.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    void navigate({
                      to: "/jerboa-editor-session/$id",
                      params: { id: s.id },
                    })
                  }
                >
                  <TableCell className="font-mono text-xs">
                    {formatTimestamp(s.createdAt as unknown as string)}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {s.projectId ? (
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: s.projectId }}
                        className="text-blue-600 hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {s.projectId}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{formatDurationMs(s.sessionDurationMs)}</TableCell>
                  <TableCell
                    className={
                      s.trigger === "crash"
                        ? "font-mono text-xs font-semibold text-destructive"
                        : "font-mono text-xs"
                    }
                  >
                    {s.trigger}
                  </TableCell>
                  <TableCell className="text-right">{formatMb(s.peakWasmReservedMb)}</TableCell>
                  <TableCell className="text-right">{formatMb(s.peakWasmLiveMb)}</TableCell>
                  <TableCell className="text-right">{s.totalActionsApplied}</TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
